import { CSSInterpolation } from '@emotion/css/create-instance'
import React, { type ReactNode, type Ref } from 'react'

import { UnstyledAnchor } from '../../components/UnstyledAnchor'
import { UnstyledButton } from '../../components/UnstyledButton'
import { type ReactAnchorProps, type ReactButtonProps } from '../../types'

type BaseProps = {
  /**
   * The React children to render inside this `BaseButton`.
   */
  children: ReactNode
  /**
   * Whether this `BaseButton` should be loading, disabled, or neither.
   */
  state?: 'default' | 'loading' | 'disabled' | false
  /**
   * Pass a string CSS class name to apply to the root element. Whenever possible, apply
   * styling via the `overrideRootStyles` prop; this hook should only be used for the class
   * names provided by components like `PrimaryColorOverride` or `LowOpacityHoverState`.
   */
  className?: string
  /**
   * Pass a CSS object to override styles on the root element. Whenever possible, you should
   * style a `BaseButton` via this prop rather than by wrapping it in another element.
   */
  overrideRootStyles?: CSSInterpolation
}

type OmittedElementPropKeys =
  | keyof BaseProps
  | 'ref'
  | 'disabled'
  | 'className'
  | 'style'
  | 'css'
  | 'type'

type AnchorProps = {
  type: 'anchor'
  innerRef?: Ref<HTMLAnchorElement>
} & Omit<ReactAnchorProps, OmittedElementPropKeys>

type ButtonProps = {
  type: 'button' | 'submit' | 'reset'
  innerRef?: Ref<HTMLButtonElement>
} & Omit<ReactButtonProps, OmittedElementPropKeys>

type Props = BaseProps & (AnchorProps | ButtonProps)

/**
 * A `BaseButton` is the core underlying component for all our `*Button` components.
 *
 * You almost certainly don't want to use this component directly.
 *
 * `Button`, `TextButton`, and `IconButton` cover the vast majority of button use cases. You
 * should use this only when those other components don't suffice because you truly need deep
 * custom styling for your button, which should be very rare. To style it for your use case,
 * use the `overrideRootStyles` prop.
 */
export function BaseButton({
  children,
  className,
  overrideRootStyles,
  state: stateProp = 'default',
  ...props
}: Props) {
  const state = stateProp || 'default'

  const css: CSSInterpolation = [
    {
      alignItems: 'center',
      boxSizing: 'border-box',
      display: 'inline-flex',
      justifyContent: 'center',
    },
    state === 'default' && {
      cursor: 'pointer',
    },
  ]

  if (props.type === 'anchor') {
    const { innerRef, type, ...anchorProps } = props

    // NOTE(eric.lovett): Anchor tags don't accept a "disabled" attribute, so if we're
    // disabling the anchor version of the button, we need to fake a disabled state while
    // still making sure the element can receive pointer events.
    if (state === 'disabled' || state === 'loading') {
      delete anchorProps.href
      delete anchorProps.onClick
      anchorProps.tabIndex = -1
      anchorProps.onFocus = e => {
        e.preventDefault()
        e.target.blur()
      }
    }
    if (state === 'disabled') {
      anchorProps['aria-disabled'] = true
    }
    if (state === 'loading') {
      anchorProps['aria-busy'] = true
      anchorProps['aria-live'] = 'polite'
    }

    return (
      <UnstyledAnchor
        // NOTE(eric.lovett): Since we remove the href attribute when this is disabled,
        // it won't have a "link" role, so we force it here so users will know it's a
        // link that's been disabled (communicated via aria-disabled). This is just
        // a guess at what a good experience might be. We should revisit this after
        // an accessibility audit.
        role="link"
        {...anchorProps}
        ref={innerRef}
        className={className}
        css={[css, overrideRootStyles]}
      >
        {children}
      </UnstyledAnchor>
    )
  }

  const { innerRef, ...buttonProps } = props
  return (
    <UnstyledButton
      {...buttonProps}
      ref={innerRef}
      aria-busy={state === 'loading'}
      className={className}
      css={[css, overrideRootStyles]}
      disabled={state === 'loading' || state === 'disabled'}
    >
      {children}
    </UnstyledButton>
  )
}
